import {
    CheckMarkIcon,
    MoneyIcon,
    MoreVerticalIcon,
    PictureFillIcon
} from "@fluentui/react-icons-mdl2"
import { DefaultButton } from "@fluentui/react/lib/Button"
import { DirectionalHint } from "@fluentui/react/lib/ContextualMenu"
import {
    IProcessedStyleSet,
    mergeStyles,
    registerIcons
} from "@fluentui/react/lib/Styling"
import { classNamesFunction, styled } from "@fluentui/react/lib/Utilities"
import { Bus } from "baconjs"
import React, { useEffect, useState } from "react"

import { L10n } from "@encoway/l10n"

import { Events } from "../../index"
import { HeaderMenuStyles } from "./HeaderMenu.styles"
import { HeaderMenuProps, IHeaderMenuStyles } from "./HeaderMenu.types"

const MENU = "MoreVertical"
const CHECKMARK = "Checkmark"
const TOGGLE_PRICES = "Money"
const TOGGLE_VISUALIZATION = "PictureFill"
registerIcons(
    {
        icons: {
            [MENU]: <MoreVerticalIcon />,
            [CHECKMARK]: <CheckMarkIcon />,
            [TOGGLE_PRICES]: <MoneyIcon />,
            [TOGGLE_VISUALIZATION]: <PictureFillIcon />
        }
    },
    { disableWarnings: true }
)

const ToggleVisualizationItem = (checked: boolean, eventBus: Bus<unknown>) => ({
    key: "toggleVisualization",
    iconProps: { iconName: TOGGLE_VISUALIZATION },
    text: L10n.format("Application.Header.HeaderMenu.visualization"),
    canCheck: true,
    isChecked: checked,
    onClick: () =>
        checked
            ? eventBus.push(Events.HideVisualization)
            : eventBus.push(Events.ShowVisualization)
})

const TogglePricesItem = (checked: boolean, eventBus: Bus<unknown>) => ({
    key: "togglePrices",
    iconProps: { iconName: TOGGLE_PRICES },
    text: L10n.format("Application.Header.HeaderMenu.prices"),
    canCheck: true,
    isChecked: checked,
    onClick: () =>
        checked
            ? eventBus.push(Events.HidePrices)
            : eventBus.push(Events.ShowPrices)
})

/**
 * The HeaderMenu renders a callout menu which allows to toggle the visualization and the prices.
 *
 * The selected state of the visualization and the prices are stored in the browser storage per user.
 *
 * On each toggle events are pushed to the bus. Checkout the `Events` constants in this project.
 *
 * Links:
 * - [Checkout the code](https://gitlab.encoway-services.de/pd/dev/encoway-cpq/-/blob/master/cui/features/application-components/src/components/HeaderMenu/HeaderMenu.tsx)
 * - [HeaderMenuStyles](https://gitlab.encoway-services.de/pd/dev/encoway-cpq/-/blob/master/cui/features/application-components/src/components/HeaderMenu/HeaderMenu.styles.ts)
 * - [HeaderMenuProps](https://gitlab.encoway-services.de/pd/dev/encoway-cpq/-/blob/master/cui/features/application-components/src/components/HeaderMenu/HeaderMenu.types.ts)
 * - [MS Fluent Button](https://developer.microsoft.com/de-DE/fluentui#/controls/web/button)
 *
 * @visibleName HeaderMenu
 */
const IHeaderMenu = (props: HeaderMenuProps) => {
    const { styles, theme, eventBus } = props
    const classNames = classNamesFunction()(
        styles,
        theme
    ) as IProcessedStyleSet<IHeaderMenuStyles>

    const [showVisualization, setShowVisualization] = useState(
        props.visualizationEnabled && props.showVisualization
    )
    const [showPrices, setShowPrices] = useState(props.showPrices)
    const [isOpen, setIsOpen] = useState(false)

    useEffect(() => {
        const unregister = eventBus.onValue((e) => {
            switch (e) {
                case Events.HidePrices:
                    setShowPrices(false)
                    break
                case Events.ShowPrices:
                    setShowPrices(true)
                    break
                case Events.HideVisualization:
                    setShowVisualization(false)
                    break
                case Events.ShowVisualization:
                    setShowVisualization(props.visualizationEnabled)
                    break
            }
        })
        return () => unregister()
    }, [])

    const items = []

    if (props.visualizationEnabled) {
        items.push(ToggleVisualizationItem(showVisualization, eventBus))
    }

    items.push(TogglePricesItem(showPrices, eventBus))

    let className = classNames.button
    if (isOpen) {
        className = mergeStyles(classNames.button, classNames.menuOpen)
    }

    return (
        <DefaultButton
            className={className}
            menuProps={{
                items,
                directionalHint: DirectionalHint.bottomRightEdge
            }}
            menuIconProps={{
                iconName: MENU
            }}
            onMenuClick={() => setIsOpen(true)}
            onAfterMenuDismiss={() => setIsOpen(false)}
        />
    )
}

export const HeaderMenu = styled(IHeaderMenu, HeaderMenuStyles)
